// Replace your existing useMathFormulasFetch.js with this:

import { useState, useCallback } from 'react';
import { useAuth } from '../fetches/AuthorizationFetch';

// Global cache that persists between component renders and even unmounts
const globalCache = {
  data: {},
  inFlight: {}
};

const useMathFormulas = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const { authenticatedFetch } = useAuth();

  // Create a stable fetch function
  const getMathFormulas = useCallback(async (userId) => {
    if (!userId) {
      throw new Error('No user ID provided');
    }
    
    // Check the global cache first
    const now = Date.now();
    const cachedItem = globalCache.data[userId];
    if (cachedItem && cachedItem.expiry > now) {
      return cachedItem.data;
    }
    
    // Check for in-flight requests
    if (globalCache.inFlight[userId]) {
      return globalCache.inFlight[userId];
    }
    
    // Create the fetch promise
    const fetchPromise = (async () => {
      try {
        setIsLoading(true);
        
        const response = await authenticatedFetch(
          `https://backend-sumisni.ota-technology.com/analyst/math-formulas/${userId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Failed to fetch math formulas for ID: ${userId}`);
        }
        
        const data = await response.json();
        
        // Cache for 10 minutes
        globalCache.data[userId] = {
          data,
          expiry: now + 600000 // 10 minutes
        };
        
        return data;
      } catch (error) {
        console.error(`Error fetching formulas for ${userId}:`, error);
        setFetchError(error.message);
        throw error;
      } finally {
        setIsLoading(false);
        delete globalCache.inFlight[userId];
      }
    })();
    
    // Store the promise in the in-flight cache
    globalCache.inFlight[userId] = fetchPromise;
    
    return fetchPromise;
  }, [authenticatedFetch]);
  
  return { getMathFormulas, isLoading, fetchError };
};

export default useMathFormulas;